/**
 * Shared constants for Ctrl Enter application
 *
 * This module contains constants that are used across both
 * the desktop client and service-analysis server.
 */

/**
 * Chat attachment and request size limits.
 * Applies to all supported upload types (images, PDFs, spreadsheets, etc.).
 * Prevents 413 "Request body too large" errors.
 */
export const ATTACHMENT_LIMITS = {
  // Maximum file size for a single attachment (50MB)
  MAX_FILE_SIZE: 50 * 1024 * 1024,

  // Maximum size per attachment in KB (20MB) — generous for high-quality medical images
  MAX_ATTACHMENT_SIZE_KB: 20 * 1024,

  // Maximum total request size in KB (100MB) — generous for no-retention local storage
  MAX_TOTAL_REQUEST_SIZE_KB: 100 * 1024,

  // Maximum attachments per message (existing context + newly attached)
  MAX_ATTACHMENTS_PER_REQUEST: 100,

  // Maximum new attachments per upload action
  MAX_NEW_ATTACHMENTS_PER_REQUEST: 100,
} as const;

/**
 * Server Configuration Constants
 */
export const SERVER_LIMITS = {
  // HTTP request body size limit (matches ATTACHMENT_LIMITS.MAX_TOTAL_REQUEST_SIZE_KB)
  MAX_REQUEST_SIZE: ATTACHMENT_LIMITS.MAX_TOTAL_REQUEST_SIZE_KB * 1024, // Convert to bytes

  // Request timeout (5 minutes - plenty of time for large uploads)
  REQUEST_TIMEOUT: 300000,
} as const;

/**
 * Supported file types for image uploads (MIME types)
 */
export const SUPPORTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
] as const;

/**
 * Supported file types for PDF uploads (MIME types)
 */
export const SUPPORTED_PDF_TYPES = ['application/pdf'] as const;

/**
 * Supported document types that require text extraction (MIME types)
 */
export const SUPPORTED_DOCUMENT_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel', // .xls
  'text/csv',
  'text/plain',
] as const;

/**
 * All supported file types for uploads - MIME types (images, PDFs, and extractable documents)
 */
export const SUPPORTED_FILE_TYPES = [
  ...SUPPORTED_IMAGE_TYPES,
  ...SUPPORTED_PDF_TYPES,
  ...SUPPORTED_DOCUMENT_TYPES,
] as const;

/**
 * Supported image file extensions (for file picker accept attribute)
 * Some browsers/systems need explicit extensions in addition to MIME types
 */
export const SUPPORTED_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp', '.gif'] as const;

/**
 * Supported PDF file extensions (for file picker accept attribute)
 */
export const SUPPORTED_PDF_EXTENSIONS = ['.pdf'] as const;

/**
 * Supported document file extensions (for file picker accept attribute)
 */
export const SUPPORTED_DOCUMENT_EXTENSIONS = ['.xlsx', '.xls', '.csv', '.txt'] as const;

/**
 * All supported file extensions (for file picker accept attribute)
 * Combines MIME types and extensions for maximum browser compatibility
 */
export const SUPPORTED_FILE_ACCEPT = [
  ...SUPPORTED_FILE_TYPES,
  ...SUPPORTED_IMAGE_EXTENSIONS,
  ...SUPPORTED_PDF_EXTENSIONS,
  ...SUPPORTED_DOCUMENT_EXTENSIONS,
] as const;

/**
 * MIME type validation map (backward compatibility)
 * @deprecated Use SUPPORTED_FILE_TYPES for mixed file support
 */
export const SUPPORTED_MIME_TYPES = SUPPORTED_IMAGE_TYPES;

/**
 * Image compression settings
 */
export const COMPRESSION_SETTINGS = {
  // Default image dimensions for compression
  DEFAULT_MAX_WIDTH: 1920,
  DEFAULT_MAX_HEIGHT: 1080,

  // Aggressive compression dimensions for very large images
  AGGRESSIVE_MAX_WIDTH: 1280,
  AGGRESSIVE_MAX_HEIGHT: 720,

  // Quality settings
  DEFAULT_QUALITY: 0.85,
  AGGRESSIVE_QUALITY: 0.7,
  MIN_QUALITY: 0.3,

  // Quality reduction step
  QUALITY_STEP: 0.1,

  // Dimension reduction factor when quality reduction isn't enough
  DIMENSION_REDUCTION_FACTOR: 0.9,

  // Quality reset value when resizing
  QUALITY_RESET: 0.6,
} as const;

/**
 * Export all constants as a single object for convenience
 */
export const ENTER_CTRL_CONSTANTS = {
  ATTACHMENT_LIMITS,
  SERVER_LIMITS,
  SUPPORTED_IMAGE_TYPES,
  SUPPORTED_PDF_TYPES,
  SUPPORTED_DOCUMENT_TYPES,
  SUPPORTED_FILE_TYPES,
  SUPPORTED_IMAGE_EXTENSIONS,
  SUPPORTED_PDF_EXTENSIONS,
  SUPPORTED_DOCUMENT_EXTENSIONS,
  SUPPORTED_FILE_ACCEPT,
  SUPPORTED_MIME_TYPES,
  COMPRESSION_SETTINGS,
} as const;

export type EnterCtrlConstants = typeof ENTER_CTRL_CONSTANTS;

export const COOKIE_CONSTANTS = {
  SESSION_COOKIE_NAME: 'wos-session',
  SESSION_COOKIE_MAX_AGE: 60 * 60 * 24 * 7, // 7 days
} as const;
